import React, { useEffect, useState } from "react";
import "../FiltersSidebar/FiltersSidebar.css";
import { FaSearch } from "react-icons/fa";
import Select from "react-select";

import { DEFAULT_SPORT, GAME_POSITIONS } from "../../Utils/Constants";
import { getFilters } from "../../Utils/api";

/**
 * Filter sidebar.
 *
 * Every option now comes from ONE request to /v1/filters. Previously changing
 * the sport fired four (conferences_in_sport, unique_player_class_by_sport,
 * sports/{id}/positions, get_all_teams_in_sport) plus a fifth for teams in a
 * conference — on the most-used control in the product — spread across five
 * fetch functions and four overlapping effects, two of which called
 * `getAllTeamsSports()` with no argument and silently did nothing.
 *
 * Filter state lives in the URL (see Dashboard), so a filtered view can be
 * shared and Back restores it.
 */
const FiltersSidebar = ({
  loading,
  filterState,
  setFilterState,
  handleSearchPlayers,
  playerFilter,
  setPlayerFilter,
  resultCount,
}) => {
  const [options, setOptions] = useState({
    sports: [],
    conferences: [],
    teams: [],
    positions: [],
    classes: [],
  });

  const sportId = filterState?.sport?.value;

  useEffect(() => {
    let cancelled = false;
    getFilters(sportId ? { sport_id: sportId } : undefined)
      .then((data) => {
        if (!cancelled) setOptions(data);
      })
      .catch((error) => console.error("Failed to load filters:", error));
    return () => {
      cancelled = true;
    };
  }, [sportId]);

  // Teams narrow to the selected conference. Done client-side because
  // /v1/filters already returned every team with its conference — no second
  // request needed.
  const teamOptions = (options.teams || [])
    .filter(
      (t) =>
        !filterState?.conference?.value ||
        t.conference === filterState.conference.value
    )
    .map((t) => ({
      value: t.team_id,
      label: t.team_name,
      conference: t.conference,
    }));

  const asOptions = (values) =>
    (values || []).map((v) => ({ value: v, label: String(v) }));

  const positionOptions = (options.positions || []).map((p) => ({
    value: p,
    label: GAME_POSITIONS[p] ? `${p} (${GAME_POSITIONS[p]})` : p,
  }));

  const patch = (changes) => setFilterState((prev) => ({ ...prev, ...changes }));

  const activeChips = [
    filterState?.conference?.value && {
      key: "conference",
      label: filterState.conference.label,
      clear: () => patch({ conference: "", team: "" }),
    },
    filterState?.team?.value && {
      key: "team",
      label: filterState.team.label,
      clear: () => patch({ team: "" }),
    },
    filterState?.class?.value && {
      key: "class",
      label: filterState.class.label,
      clear: () => patch({ class: "" }),
    },
    filterState?.position?.value && {
      key: "position",
      label: filterState.position.label,
      clear: () => patch({ position: "" }),
    },
    playerFilter?.trim() && {
      key: "q",
      label: `"${playerFilter.trim()}"`,
      clear: () => setPlayerFilter(""),
    },
  ].filter(Boolean);

  const clearAll = () => {
    setPlayerFilter("");
    setFilterState({
      sport: DEFAULT_SPORT,
      conference: "",
      team: "",
      class: "",
      position: "",
    });
  };

  return (
    <div className="main-filter-sidebar">
      <div className="search_filter_player_outer">
        <div className="find-loc-input-main search_filter_player_outer">
          <input
            type="search"
            className="form-control-input"
            value={playerFilter}
            onChange={(e) => setPlayerFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearchPlayers();
            }}
            placeholder="Search Athlete"
            name="search"
          />
          <div className="search-icon-player" onClick={handleSearchPlayers}>
            <FaSearch />
          </div>
        </div>
      </div>

      {/* The result count belongs next to the controls that change it. */}
      <div className="filter-count">
        {loading
          ? "Searching…"
          : `${(resultCount ?? 0).toLocaleString()} athletes`}
      </div>

      {/* Previously there was no single place showing what was applied — the
          only affordance was scrolling the sidebar and reading five dropdowns. */}
      {activeChips.length ? (
        <div className="filter-chips">
          {activeChips.map((chip) => (
            <button key={chip.key} className="filter-chip" onClick={chip.clear}>
              {chip.label}
              <span aria-hidden="true">×</span>
            </button>
          ))}
          <button className="filter-chip-clear" onClick={clearAll}>
            Clear all
          </button>
        </div>
      ) : null}

      <div>
        <div className="find-loc-head">Sport</div>
        <div className="find-loc-input-main">
          <Select
            options={(options.sports || []).map((s) => ({
              value: s.sport_id,
              label: s.sport_name,
            }))}
            className="form-control-input-select"
            placeholder="Select Sport"
            value={filterState?.sport}
            classNamePrefix="filter_nil"
            onChange={(selected) =>
              // A new sport invalidates everything narrower.
              setFilterState({
                sport: selected,
                conference: "",
                team: "",
                class: "",
                position: "",
              })
            }
          />
        </div>
      </div>

      <div>
        <div className="find-loc-head">Conference</div>
        <div className="find-loc-input-main">
          <Select
            options={asOptions(options.conferences)}
            isClearable
            className="form-control-input-select"
            placeholder="Any conference"
            value={filterState?.conference || null}
            classNamePrefix="filter_nil"
            onChange={(selected) =>
              patch({ conference: selected || "", team: "" })
            }
          />
        </div>
      </div>

      <div>
        <div className="find-loc-head">Team</div>
        <div className="find-loc-input-main">
          <Select
            options={teamOptions}
            isClearable
            isSearchable
            className="form-control-input-select"
            placeholder="Any team"
            value={filterState?.team || null}
            classNamePrefix="filter_nil"
            onChange={(selected) => {
              const changes = { team: selected || "" };
              // Back-fill the conference from the team, but never overwrite one
              // the user picked themselves.
              if (selected?.conference && !filterState?.conference?.value) {
                changes.conference = {
                  value: selected.conference,
                  label: selected.conference,
                };
              }
              patch(changes);
            }}
          />
        </div>
      </div>

      <div>
        <div className="find-loc-head">Class</div>
        <div className="find-loc-input-main">
          <Select
            options={asOptions(options.classes)}
            isClearable
            className="form-control-input-select"
            placeholder="Any class"
            value={filterState?.class || null}
            classNamePrefix="filter_nil"
            onChange={(selected) => patch({ class: selected || "" })}
          />
        </div>
      </div>

      <div>
        <div className="find-loc-head">Position</div>
        <div className="find-loc-input-main">
          <Select
            options={positionOptions}
            isClearable
            isSearchable
            className="form-control-input-select"
            placeholder="Any position"
            value={filterState?.position || null}
            classNamePrefix="filter_nil"
            onChange={(selected) => patch({ position: selected || "" })}
          />
        </div>
      </div>

      <div className="search-clr-filter-btn">
        <button className="search-clr-filter-btn-inputs" onClick={clearAll}>
          Clear filters
        </button>
      </div>
    </div>
  );
};

export default FiltersSidebar;
